// src/pages/ClientDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import Axios from "../utils/Axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import "../styles/EmployeeDetail.css";
import Toast from "../utils/Toast";

import AuthHandler from "../utils/AuthHandler";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";

import { useSelector } from "react-redux";
import AccessRestricted from "../components/AccessRestricted";
import validateForm, { IsValidEmail } from "../utils/Validation";
import { EDIT_CLIENT, DELETE_CLIENT } from "../config/API_Collection";

import { ClientProjects } from "../components/ClientProjects";

function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="employee-detail-container p-3">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RestrictedLayout({ homePath = "projects" }) {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath={homePath} />
        </div>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id: empid } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  // ----- Auth / permissions -----
  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null;
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canViewClient = actions?.clients?.client_list === true;
  const canEditClient = actions?.clients?.edit_client === true;
  const canDeleteClient = actions?.clients?.delete_client === true;

  // ----- Local state -----
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "", triggerId: 0 });
  const [selectedTab, setSelectedTab] = useState("projects");

  // Submit locks for modals
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  // Modal state
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalRemove, setShowModalRemove] = useState(false);

  // Form errors (edit)
  const [formErrors, setFormErrors] = useState({});

  // Preload client from navigation state or sessionStorage
  const initialClient = useMemo(() => {
    if (state?.client) return state.client;
    try {
      const raw = sessionStorage.getItem("client.preload");
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }, [state]);

  const [client, setClient] = useState(initialClient);

  const showToast = (message, type) =>
    setToast({ message, type, triggerId: Date.now() });

  useEffect(() => {
    if (state?.client) {
      console.log(
        "ClientDetail: received client from navigation state:",
        state.client
      );
    } else {
      console.log(
        "ClientDetail: no client in navigation state. Using sessionStorage or fallback."
      );
    }
    if (initialClient) {
      try {
        sessionStorage.setItem("client.preload", JSON.stringify(initialClient));
      } catch {}
    }
  }, [state, initialClient]);

  // ------- Edit modal seed values -------
  const [editData, setEditData] = useState({
    client_id: "",
    client_name: "",
    email: "",
    phone: "",
    address: "",
  });

  const openEditModal = () => {
    if (!client) return;
    setEditData({
      client_id: client.client_id,
      client_name: client.client_name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
    });
    setFormErrors({});
    setShowModalEdit(true);
  };
  const closeEditModal = () => {
    setShowModalEdit(false);
    setIsSubmittingEdit(false);
  };

  const openDeleteModal = () => setShowModalRemove(true);
  const closeDeleteModal = () => setShowModalRemove(false);

  // ------- Handlers (Edit/Delete) -------
  const handleEditClient = async (e) => {
    e.preventDefault();
    if (isSubmittingEdit) return;
    setIsSubmittingEdit(true);

    try {
      const form = e.target;
      const payload = {
        client_id: editData.client_id,
        client_name: form.client_name.value,
        email: form.email.value,
        phone: form.phone.value,
        address: form.address.value,
      };

      const required = ["client_name", "email", "phone", "address"];
      const errors = validateForm(payload, required);
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        setIsSubmittingEdit(false);
        return;
      }
      if (!IsValidEmail(payload.email)) {
        showToast("Enter Valid Email Address!", "failed");
        setIsSubmittingEdit(false);
        return;
      }

      const res = await Axios.post(EDIT_CLIENT, payload);
      if (res.status === 200) {
        // Update local header immediately
        const updated = { ...client, ...payload };
        setClient(updated);
        try {
          sessionStorage.setItem("client.preload", JSON.stringify(updated));
        } catch {}
        showToast("Client Updated Successfully!", "success");
        setIsSubmittingEdit(false);
        closeEditModal();
        return; // keep disabled, modal closes
      }

      showToast(res?.data?.message || "Failed to update client", "failed");
      setIsSubmittingEdit(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update client",
        "failed"
      );
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteClient = async () => {
    if (isSubmittingDelete) return;
    setIsSubmittingDelete(true);
    try {
      if (!client?.client_id) {
        showToast("Missing client id.", "failed");
        setIsSubmittingDelete(false);
        return;
      }
      const payload = { client_id: client.client_id };
      const res = await Axios.post(DELETE_CLIENT, payload);
      if (res.status === 200) {
        showToast("Client Deleted Successfully!", "success");
        closeDeleteModal();
        navigate(-1); // go back to list
        return;
      }
      showToast(res?.data?.message || "Failed to delete client", "failed");
      setIsSubmittingDelete(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete client",
        "failed"
      );
      setIsSubmittingDelete(false);
    }
  };

  const getActiveLinkClass = (tab) =>
    selectedTab === tab ? "tab-button active-tab" : "tab-button";

  // ----- Early returns -----
  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canViewClient) return <RestrictedLayout homePath="projects" />;

  const statusText =
    client?.status === 1
      ? "Active"
      : client?.status === 4
      ? "Blocked"
      : client?.status === 3
      ? "Inactive"
      : "";

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav pathName={"Client Details"} />
        {isLoading && <div className="top-loading-bar" />}
        <Toast
          message={toast.message}
          type={toast.type}
          triggerId={toast.triggerId}
        />

        <div className="employee-detail-container p-3">
          {client ? (
            <>
              <div className="row align-items-center">
                <div className="col-md-2 text-center">
                  <img
                    src="/assets/images/profile_user.jpg"
                    alt="Profile"
                    className="profile-pic-detail-view"
                  />
                </div>

                <div className="col-md-6">
                  <h4 className="employee-name">{client.client_name}</h4>

                  <div className="text-smaller text-secondary">
                    <div>
                      <strong>Email:</strong> {client.email || "-"}
                    </div>
                    <div>
                      <strong>Phone:</strong> {client.phone || "-"}
                    </div>
                    <div>
                      <strong>Address:</strong> {client.address || "-"}
                    </div>
                    <div>
                      <strong>Status:</strong> {statusText}
                    </div>
                    <div>
                      <strong>Registered:</strong> {client.created_at || "-"}
                    </div>
                  </div>
                </div>

                <div className="col-md-3" />
                <div className="col-md-1 d-flex justify-content-end">
                  <div className="dropdown">
                    <button
                      className="btn btn-sm"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi bi-three-dots-vertical" />
                    </button>
                    <ul className="dropdown-menu">
                      {canEditClient && (
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={openEditModal}
                          >
                            Edit Client
                          </button>
                        </li>
                      )}
                      {canDeleteClient && (
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={openDeleteModal}
                          >
                            Delete Client
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="row align-items-center">
                <hr className="prf-sep-hr" />
              </div>
            </>
          ) : (
            <div
              className="alert alert-warning d-flex justify-content-between align-items-center"
              role="alert"
            >
              <div>No client data available.</div>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => navigate(-1)}
              >
                Go Back
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="project-detail-container">
          <div className="tab-nav-container">
            {["projects" /*, "invoices", "documents", "communication" */].map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={getActiveLinkClass(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              )
            )}
          </div>

          <div className="content-section">
            {selectedTab === "projects" && <ClientProjects empid={empid} />}
          </div>
        </div>
      </div>

      {/* -------- Edit Client Modal -------- */}
      {showModalEdit && canEditClient && (
        <div className="custom-project-modal">
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            tabIndex="-1"
            aria-hidden="true"
            style={{ display: "block", zIndex: 1050 }}
          >
            <div
              className="modal-dialog modal-lg mx-auto"
              style={{ marginTop: "2%" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Client</h5>
                </div>
                <div className="modal-body">
                  <div className="add-budget-container">
                    <form
                      noValidate
                      className="budget-form"
                      onSubmit={handleEditClient}
                    >
                      <fieldset
                        disabled={isSubmittingEdit}
                        style={{ border: 0, padding: 0, margin: 0 }}
                      >
                        <div className="form-row">
                          <div
                            className={`form-group flex-item ${
                              formErrors.client_name ? "has-error" : ""
                            }`}
                          >
                            <label>Client Name *</label>
                            <input
                              type="text"
                              name="client_name"
                              defaultValue={editData.client_name}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div
                            className={`form-group flex-item ${
                              formErrors.email ? "has-error" : ""
                            }`}
                          >
                            <label>Email *</label>
                            <input
                              type="text"
                              name="email"
                              defaultValue={editData.email}
                            />
                          </div>
                          <div
                            className={`form-group flex-item ${
                              formErrors.phone ? "has-error" : ""
                            }`}
                          >
                            <label>Phone *</label>
                            <input
                              type="text"
                              name="phone"
                              defaultValue={editData.phone}
                              minLength={9}
                              maxLength={12}
                            />
                          </div>
                          <div
                            className={`form-group flex-item ${
                              formErrors.address ? "has-error" : ""
                            }`}
                          >
                            <label>Address *</label>
                            <input
                              type="text"
                              name="address"
                              defaultValue={editData.address}
                            />
                          </div>
                        </div>

                        <div className="d-flex justify-content-center">
                          {" "}
                          <button type="submit" className="btn btn-primary">
                            {isSubmittingEdit ? "Updating..." : "Update Client"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary ms-2"
                            onClick={closeEditModal}
                          >
                            Cancel
                          </button>
                        </div>
                      </fieldset>

                      {/* Cancel stays enabled */}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* -------- Delete Client Modal -------- */}
      {showModalRemove && canDeleteClient && (
        <div className="custom-project-modal">
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            tabIndex="-1"
            aria-hidden="true"
            style={{ display: "block", zIndex: 1050 }}
          >
            <div className="modal-dialog  mx-auto" style={{ marginTop: "15%" }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Client Deletion</h5>
                </div>
                <div className="modal-body text-center">
                  <p>Are you sure you want to delete this Client?</p>
                </div>
                <div className="d-flex justify-content-center gap-5">
                  <button
                    className="btn btn-danger px-5"
                    onClick={handleDeleteClient}
                  >
                    "Yes"
                  </button>

                  <button
                    className="btn btn-secondary px-5"
                    onClick={closeDeleteModal}
                  >
                    No
                  </button>
                </div>
                <div className="py-3" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
